import React, {
  FC,
  KeyboardEvent,
  useCallback,
  useEffect,
  useRef
} from "react";
import styled from "styled-components";

import i18n from "../../../service/i18n";
import { usePlayerStore } from "../../../store/usePlayerStore";
import {
  ElapsedProgress as VolumeLevel,
  Progress as Volume,
  SeekSlider,
  Wrapper
} from "../../share/ProgressBar";
import MuteButton from "../AudioVolume/MuteButton";
import { ThemeColors } from "../getThemeColors";

const VolumeControls = styled.div<{
  showVolumeSlider: boolean;
}>`
  align-items: center;
  background-color: transparent;
  display: flex;
  flex-direction: row-reverse;
  flex-grow: ${props => (props.showVolumeSlider ? 1 : 0)};
  transition: flex-grow 400ms;
`;

interface Props {
  language: string;
  setShowVolumeSlider: (isHovered: boolean) => void;
  showVolumeSlider: boolean;
  themeColors: ThemeColors;
}

const AudioVolume: FC<Props> = ({
  language,
  setShowVolumeSlider,
  showVolumeSlider,
  themeColors
}) => {
  const { useStoreState } = usePlayerStore();
  const device = useStoreState.device();
  const isMuted = useStoreState.isMuted();
  const player = useStoreState.player();
  const volume = useStoreState.volume();

  const timerRef = useRef<NodeJS.Timeout>(undefined);
  const volumeControlsRef = useRef<HTMLDivElement>(null);
  const showVolumeSliderRef = useRef<boolean>(undefined);
  showVolumeSliderRef.current = showVolumeSlider;

  const onKeyEvent = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key.startsWith("Arrow")) {
      e.preventDefault();
    }
  };

  const focusEventHandler = useCallback(
    event => {
      if (
        showVolumeSliderRef.current &&
        volumeControlsRef.current &&
        !volumeControlsRef.current.contains(event.target)
      ) {
        setShowVolumeSlider(false);
      }
    },
    [setShowVolumeSlider]
  );

  useEffect(() => {
    document.addEventListener("click", focusEventHandler);
    document.addEventListener("focusin", focusEventHandler);
    return () => {
      document.removeEventListener("click", focusEventHandler);
      document.removeEventListener("focusin", focusEventHandler);
    };
  }, [focusEventHandler]);

  useEffect(
    () => () => {
      clearTimeout(timerRef.current as NodeJS.Timeout);
    },
    []
  );

  const volumeButtonAriaLabel = i18n(
    isMuted ? "poista_mykistys_aria_label" : "mykista_aria_label",
    {
      language
    }
  );

  const { fillColor, volumeBarBackgroundColor, volumeLevelColor } = themeColors;

  if (device.isMobile) {
    return (
      <MuteButton
        aria-keyshortcuts="m"
        aria-label={volumeButtonAriaLabel}
        fillColor={fillColor}
        isMobile={device.isMobile}
      />
    );
  } else {
    return (
      <VolumeControls
        data-testid="volume-controls"
        onMouseOver={() => clearTimeout(timerRef.current as NodeJS.Timeout)}
        onMouseLeave={() => {
          timerRef.current = setTimeout(() => {
            setShowVolumeSlider(false);
          }, 1000);
        }}
        ref={volumeControlsRef}
        showVolumeSlider={showVolumeSlider}>
        <MuteButton
          aria-keyshortcuts="m"
          aria-label={volumeButtonAriaLabel}
          fillColor={fillColor}
          isMobile={device.isMobile}
          onFocus={() => !showVolumeSlider && setShowVolumeSlider(true)}
          onMouseOver={() => !showVolumeSlider && setShowVolumeSlider(true)}
        />

        <Volume
          isMobile={device.isMobile}
          visibility={showVolumeSlider ? "visible" : "hidden"}
          margin={!showVolumeSlider ? "0" : "0 4px"}>
          <Wrapper backgroundColor={volumeBarBackgroundColor}>
            <VolumeLevel
              backgroundColor={volumeLevelColor}
              max={1}
              value={isMuted ? 0 : volume}
            />
          </Wrapper>
          <SeekSlider
            aria-label={i18n("volume", { language })}
            background={volumeLevelColor}
            max={1}
            onInput={e =>
              player.setVolume(
                parseFloat((e.target as HTMLInputElement).value),
                true
              )
            }
            onKeyDown={onKeyEvent}
            onKeyUp={onKeyEvent}
            step={0.02}
            value={isMuted ? 0 : volume}
          />
        </Volume>
      </VolumeControls>
    );
  }
};

export default AudioVolume;
